import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field, inputCls, Badge } from "@/components/kit";
import { Upload, Building2, Image as ImageIcon, CreditCard, Loader2, ExternalLink } from "lucide-react";
import { euro } from "@/lib/api";

const EMPTY = { company_name: "", address: "", phone: "", email: "", website: "", siret: "" };

function BillingCard() {
  const { user, refreshUser } = useAuth();
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/billing/summary").then((r) => setSum(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (user?.role !== "owner") return null;

  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString("fr-FR") : null;
  const trialEnd = sum?.trial_ends_at ? new Date(sum.trial_ends_at).toLocaleDateString("fr-FR") : null;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Résilier l'abonnement à la fin de la période en cours ?")) return;
    setBusy(true);
    try { await api.post("/billing/cancel"); toast.success("Résiliation programmée"); await load(); await refreshUser(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/billing/resume"); toast.success("Abonnement réactivé"); await load(); await refreshUser(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const status = sum?.subscription_status;
  const label = status === "active" ? "Actif" : status === "trialing" ? "Essai gratuit" : status === "past_due" ? "Paiement en retard" : "Résilié";

  return (
    <div className="border border-border bg-card p-6" data-testid="billing-card">
      <div className="mb-4 flex items-center gap-2">
        <CreditCard size={18} className="text-primary" />
        <h3 className="font-display text-lg font-bold">Abonnement</h3>
      </div>
      {!sum ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 border border-border bg-background p-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Formule</p>
              <p className="font-display text-xl font-bold">GestionPro · 100€/mois</p>
              <p className="mt-1 text-sm"><Badge>{label}</Badge>
                {sum.cancel_at_period_end && <span className="ml-2 text-xs text-destructive">résiliation programmée</span>}
              </p>
            </div>
            <div className="text-right text-sm">
              {status === "trialing" && trialEnd && <p className="text-muted-foreground">Essai jusqu'au <strong className="text-foreground">{trialEnd}</strong></p>}
              {fmtDate(sum.current_period_end) && (
                <p className="text-muted-foreground">
                  {sum.cancel_at_period_end ? "Fin d'accès le " : "Prochain prélèvement le "}
                  <strong className="text-foreground">{fmtDate(sum.current_period_end)}</strong>
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {status !== "active" && (
              <Btn onClick={subscribe} loading={busy} data-testid="settings-subscribe-btn"><CreditCard size={16} /> S'abonner</Btn>
            )}
            {status === "active" && !sum.cancel_at_period_end && (
              <Btn variant="danger" onClick={cancel} loading={busy} data-testid="cancel-sub-btn">Résilier</Btn>
            )}
            {status === "active" && sum.cancel_at_period_end && (
              <Btn onClick={resume} loading={busy} data-testid="resume-sub-btn">Réactiver</Btn>
            )}
          </div>

          {sum.invoices?.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Historique de facturation</p>
              <table className="w-full text-sm">
                <tbody>
                  {sum.invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-border last:border-0" data-testid={`receipt-${inv.id}`}>
                      <td className="py-2 font-mono text-xs">{inv.number || inv.id.slice(-8)}</td>
                      <td className="py-2 text-muted-foreground">{new Date(inv.created * 1000).toLocaleDateString("fr-FR")}</td>
                      <td className="py-2 text-right font-mono">{euro(inv.amount)}</td>
                      <td className="py-2 text-right"><Badge>{inv.status === "paid" ? "payée" : inv.status}</Badge></td>
                      <td className="py-2 pl-3 text-right">
                        {inv.pdf && <a href={inv.pdf} target="_blank" rel="noreferrer" className="text-primary hover:underline"><ExternalLink size={14} /></a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(EMPTY);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoV, setLogoV] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const { has_logo, ...rest } = r.data;
      setForm({ ...EMPTY, ...rest });
      setHasLogo(!!has_logo);
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setLogoV(Date.now());
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl animate-fade-up" data-testid="settings-page">
      <div className="grid grid-cols-1 gap-6">
        <BillingCard />
        {/* Logo */}
        <div className="border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <ImageIcon size={18} className="text-primary" />
            <h3 className="font-display text-lg font-bold">Logo de l'entreprise</h3>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex h-24 w-48 items-center justify-center border border-dashed border-border bg-background">
              {hasLogo ? (
                <img src={`${API}/public/logo?v=${logoV}`} alt="Logo" className="max-h-20 max-w-44 object-contain" data-testid="logo-preview" />
              ) : (
                <span className="text-xs text-muted-foreground">Aucun logo</span>
              )}
            </div>
            {isAdmin && (
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Btn variant="secondary" onClick={() => fileRef.current?.click()} loading={uploading} data-testid="upload-logo-btn">
                  <Upload size={16} /> Téléverser un logo
                </Btn>
                <p className="mt-2 text-xs text-muted-foreground">PNG, JPG ou WEBP — max 3 Mo.<br />Affiché sur les PDF et les e-mails.</p>
              </div>
            )}
          </div>
        </div>

        {/* Company info */}
        <div className="border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <Building2 size={18} className="text-primary" />
            <h3 className="font-display text-lg font-bold">Coordonnées de l'entreprise</h3>
          </div>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nom de l'entreprise">
              <input className={inputCls} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} disabled={!isAdmin} data-testid="settings-name" />
            </Field>
            <Field label="Adresse">
              <input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!isAdmin} data-testid="settings-address" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={!isAdmin} data-testid="settings-phone" /></Field>
              <Field label="E-mail"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isAdmin} data-testid="settings-email" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Site web"><input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} disabled={!isAdmin} data-testid="settings-website" /></Field>
              <Field label="SIRET"><input className={inputCls} value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} disabled={!isAdmin} data-testid="settings-siret" /></Field>
            </div>
            {isAdmin && (
              <div className="flex justify-end border-t border-border pt-4">
                <Btn type="submit" loading={saving} data-testid="save-settings-btn">Enregistrer</Btn>
              </div>
            )}
          </form>
          {!isAdmin && <p className="mt-4 text-xs text-muted-foreground">Seul un administrateur peut modifier ces informations.</p>}
        </div>
      </div>
    </div>
  );
}
